/**
 * Denial Resolve -- standalone workflow entry point.
 *
 * 4-step shell: Choose -> Upload -> Run -> Results.
 *   - "Choose": pick a sample 835 case (Test Drive) OR jump into an existing
 *     denial case from the operational list (rendered below the workflow).
 *   - "Upload": drop in 835 ERA files. We hand them to /intake/upload, which
 *     creates a denial case the user lands on at /denials/:caseId where the
 *     v2 engine runs.
 *   - "Run" + "Results": for sample/demo cases the engine output is shown
 *     inline (rate-coded + routed) and the case-context block on the right
 *     auto-fills for chat.
 *
 * Route: /DenialResolve
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  FileDropZone,
  ResultsPanel,
  Icon,
  ExampleModal,
  getWorkflowModule,
} from '../../components/workflow';
import intakeApi from '../../components/intake/intakeApi';
import { DENIAL_RESOLVE_EXAMPLE } from '../../fixtures/exampleInputs';
import DenialsListPage from '../../components/denials/DenialsListPage';
import { getSampleById } from '../../fixtures/testCases';
import { scrubClaim } from '../../services/suiteApi';
import './suite.css';

function summariseSample(sample) {
  if (!sample) return '';
  const p = sample.payload || {};
  const r = p.remittance || {};
  const carcs = (r.carcCodes || []).join(', ');
  const rarcs = (r.rarcCodes || []).join(', ');
  const sl = p.payload?.claimInformation?.serviceLines || [];
  const dx = p.payload?.claimInformation?.healthCareCodeInformation || [];
  return [
    `DENIAL CASE — ${sample.title}`,
    `Patient: ${p.patient || '—'}`,
    `Payer: ${p.payer || '—'}`,
    `Reference: ${p.claimReference || '—'}`,
    `CARC: ${carcs || '—'}${rarcs ? ` · RARC: ${rarcs}` : ''}`,
    `Denied amount: $${r.deniedAmount ?? '—'}`,
    `Service lines: ${sl.map((l) => l.professionalService?.procedureCode).filter(Boolean).join(', ') || '—'}`,
    `Diagnoses: ${dx.map((d) => d.diagnosisCode).filter(Boolean).join(', ') || '—'}`,
    '',
    (r.adjustments || []).map((a) => `Adjustment ${a.groupCode}-${a.reasonCode}: ${a.description} ($${a.amount})`).join('\n'),
  ].join('\n');
}

function routingFromCarc(carc) {
  if (!carc) return { action: 'review', why: 'No CARC supplied' };
  const c = String(carc).toUpperCase();
  if (c.includes('27') || c.includes('31') || c.includes('29')) {
    return { action: 'eligibility-reroute', why: 'Coverage / timely-filing CARC -- not an appeal; reconcile eligibility and re-route to the correct payer.' };
  }
  if (c.includes('50') || c.includes('55') || c.includes('167')) {
    return { action: 'file-appeal', why: 'Medical necessity / non-covered CARC -- assemble appeal packet against LCD/payer policy.' };
  }
  if (c.includes('151') || c.includes('45') || c.includes('97')) {
    return { action: 'corrected-claim', why: 'Adjustment/limit CARC -- correct the units/charges and resubmit as a corrected claim.' };
  }
  if (c.includes('22') || c.includes('109')) {
    return { action: 'patient-responsibility', why: 'COB/Other payer CARC -- send to secondary or patient.' };
  }
  return { action: 'review', why: 'Unmapped CARC -- route to human review.' };
}

export default function DenialResolvePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('denial-resolve');

  const [files, setFiles] = useState([]);
  const [showExample, setShowExample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busySampleId, setBusySampleId] = useState(null);
  const [error, setError] = useState('');
  const [sample, setSample] = useState(null);
  const [scrub, setScrub] = useState(null);

  const sampleId = searchParams.get('sample');

  const runSample = async (s) => {
    setBusySampleId(s.id);
    setBusy(true); setError(''); setSample(s); setScrub(null);
    try {
      try {
        const r = await scrubClaim({ payload: s.payload.payload });
        if (r?.success) setScrub(r.data);
      } catch { /* scrub is optional -- demo still shows routing */ }
    } finally {
      setBusy(false);
      setBusySampleId(null);
    }
  };

  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'denial-resolve') runSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  const onUpload = async () => {
    if (!files.length) {
      setError('Add at least one file to upload.');
      return;
    }
    setBusy(true); setError('');
    try {
      // Partition uploads: pre-built CM denial_payload JSON files take a
      // different path (direct upsert via POST /denials, no extraction),
      // everything else (835, PDF, EDI, Excel) keeps the multipart flow.
      const jsonCases = [];
      const binaryFiles = [];
      for (const f of files) {
        if (/\.json$/i.test(f.name)) {
          let parsed = null;
          try {
            const text = await f.text();
            parsed = JSON.parse(text);
          } catch (_e) {
            parsed = null;
          }
          if (parsed && (parsed.case_id || Array.isArray(parsed.denials))) {
            jsonCases.push({ file: f, payload: parsed });
            continue;
          }
        }
        binaryFiles.push(f);
      }

      let lastCaseId = null;
      for (const { payload } of jsonCases) {
        const r = await intakeApi.submitDenialPayload(payload);
        if (!r?.success || !r.caseId) {
          throw new Error(r?.message || 'Failed to create case from JSON');
        }
        lastCaseId = r.caseId;
      }
      if (binaryFiles.length) {
        const data = await intakeApi.upload(binaryFiles, { name: '', visibility: 'private' });
        if (!data?.success || !data.caseId) throw new Error(data?.message || 'Upload failed');
        lastCaseId = data.caseId;
      }
      if (!lastCaseId) {
        throw new Error('No case was created from the selected files.');
      }
      navigate(`/intake/${lastCaseId}`);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const clearSample = () => {
    setSample(null); setScrub(null);
    if (sampleId) setSearchParams({});
  };

  const currentStep = sample ? 'results' : (files.length ? 'upload' : 'choose');
  const routing = sample ? routingFromCarc((sample.payload?.remittance?.carcCodes || [])[0]) : null;
  const chatContext = summariseSample(sample);

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      caseContext={chatContext}
    >
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">2</span>
            Upload your 835, supporting files, or a CM denial_payload.json
          </h2>
          <button type="button" className="example-link" style={{ marginLeft: 'auto' }} onClick={() => setShowExample(true)}>View Example</button>
        </header>
        <p className="wf-step-card__hint">{moduleDef.uploadHint}</p>
        <FileDropZone
          files={files}
          onFiles={setFiles}
          accept={moduleDef.accepts}
          multiple={moduleDef.multiple}
          hint="Accepted: 835 X12, EDI, Excel, CSV, PDF, ZIP, screenshots/images, or a CM denial_payload JSON case."
        />
        <div className="wf-actions">
          <button type="button" className="wf-btn" disabled={busy || !files.length} onClick={onUpload}>
            <Icon.upload /> {busy ? 'Uploading…' : 'Upload & open case'}
          </button>
          {files.length > 0 && (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setFiles([])}>
              Clear files
            </button>
          )}
        </div>
        {error && <div className="wf-error">{error}</div>}
      </section>

      {sample && (
        <ResultsPanel
          title="Engine routing & evidence"
          data={{ sample: sample.payload, scrub }}
          downloadName={`denial-resolve_${sample.id}`}
          textReport={chatContext + '\n\n--- Engine routing ---\n' + (routing ? `${routing.action.toUpperCase()}: ${routing.why}` : '')}
        >
          <dl className="wf-kv">
            <div><dt>Patient</dt><dd>{sample.payload.patient}</dd></div>
            <div><dt>Payer</dt><dd>{sample.payload.payer}</dd></div>
            <div><dt>CARC</dt><dd>{(sample.payload.remittance?.carcCodes || []).join(', ') || '—'}</dd></div>
            <div><dt>Denied amount</dt><dd>${sample.payload.remittance?.deniedAmount ?? '—'}</dd></div>
            <div><dt>Recommended action</dt><dd>{routing?.action}</dd></div>
          </dl>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 6 }}>
              Why
            </div>
            <p style={{ margin: 0, color: 'var(--dm-ink)', fontSize: 13 }}>{routing?.why}</p>
          </div>
          {scrub && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', margin: '8px 0 6px' }}>
                Pre-submission scrub
              </div>
              {(scrub.issues || []).length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--dm-ink)' }}>No additional NCCI / MUE / modifier issues detected on the underlying claim.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--dm-ink)' }}>
                  {scrub.issues.slice(0, 8).map((iss, idx) => (
                    <li key={idx}><b>{(iss.severity || 'review').toUpperCase()}</b> · {iss.message}{iss.fix ? ` — fix: ${iss.fix}` : ''}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="wf-actions">
            <Link to="/AppealBuilder" className="wf-btn">
              File the appeal <Icon.arrow />
            </Link>
            <Link to="/denials" className="wf-btn wf-btn--ghost">
              See all denial cases <Icon.arrow />
            </Link>
            <button type="button" className="wf-btn wf-btn--ghost" onClick={clearSample}>
              Clear & try another sample
            </button>
          </div>
        </ResultsPanel>
      )}

      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title" style={{ fontSize: 14 }}>
            <span className="wf-step-card__num" style={{ background: '#475569' }}>·</span>
            Existing denial cases
          </h2>
          <Link to="/denials" className="wf-btn wf-btn--ghost" style={{ padding: '5px 10px', fontSize: 12 }}>
            Open full list
          </Link>
        </header>
        <DenialsListPage />
      </section>
      <ExampleModal open={showExample} onClose={() => setShowExample(false)} title="Example: Denial Payload JSON" content={DENIAL_RESOLVE_EXAMPLE} />
    </WorkflowLayout>
  );
}
